import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  increment,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";

const buildSubsection = (id, label, description) => ({
  id,
  label,
  description,
});

const SECTION_LIST = [
  {
    id: "showcase",
    label: "Showcase",
    description: "Finished designs, highlight reels, and proud moments.",
    subsections: [
      buildSubsection("general", "General chat", "Share wins and celebrate finishes."),
      buildSubsection("finished-pieces", "Finished pieces", "Final photos, framing, and brag posts."),
      buildSubsection("before-after", "Before + after", "Show progress transformations."),
      buildSubsection("gifted-work", "Gifts", "Pieces made for loved ones."),
    ],
  },
  {
    id: "work-in-progress",
    label: "Work in progress",
    description: "Progress shots, check-ins, and accountability threads.",
    subsections: [
      buildSubsection("general", "General chat", "Share updates and ask for motivation."),
      buildSubsection("daily-checkins", "Daily check-ins", "Short progress notes and photos."),
      buildSubsection("milestones", "Milestones", "Big wins and halfway posts."),
    ],
  },
  {
    id: "color-planning",
    label: "Color planning",
    description: "Palette feedback, blend tips, and thread swaps.",
    subsections: [
      buildSubsection("general", "General chat", "Palette talk and color brainstorms."),
      buildSubsection("palette-feedback", "Palette feedback", "Get eyes on your color picks."),
      buildSubsection("thread-swaps", "Thread swaps", "Find replacements and alternates."),
    ],
  },
  {
    id: "tips-tricks",
    label: "Tips and tricks",
    description: "Technique swaps, framing hacks, and time savers.",
    subsections: [
      buildSubsection("general", "General chat", "Share quick wins and lessons learned."),
      buildSubsection("techniques", "Techniques", "Stitch methods, tension, and speed tips."),
      buildSubsection("finishing", "Finishing + framing", "Backing, framing, and display ideas."),
    ],
  },
  {
    id: "pattern-help",
    label: "Pattern help",
    description: "Ask for clarity, proofreading, and stitch guidance.",
    subsections: [
      buildSubsection("general", "General chat", "Help requests and troubleshooting."),
      buildSubsection("symbol-clarity", "Symbol clarity", "Confusing symbols and charts."),
      buildSubsection("proofreading", "Proofreading", "Check counts before you stitch."),
    ],
  },
  {
    id: "supplies",
    label: "Supplies",
    description: "Fabric finds, tool reviews, and organization ideas.",
    subsections: [
      buildSubsection("general", "General chat", "Share kits and sourcing tips."),
      buildSubsection("storage", "Storage", "Organization ideas and setups."),
      buildSubsection("reviews", "Reviews", "Tool and fabric reviews."),
    ],
  },
];
const SECTION_FILTERS = [{ id: "all", label: "All sections" }, ...SECTION_LIST];
const SUBSECTION_FILTER_ALL = { id: "all", label: "All topics" };
const DEFAULT_FORUM_SETTINGS = {
  allowImages: true,
  allowDesignShares: true,
  allowGallery: true,
  lockNewThreads: false,
  lockReplies: false,
  forumName: "Stitch Board",
  announcement: "",
};
const ATTACHMENT_LIMIT = 3;
const ATTACHMENT_PREVIEW_SIZE = 360;
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;
const MAX_ATTACHMENT_DATA_BYTES = 320 * 1024;
const MAX_THREAD_ATTACHMENT_BYTES = 800 * 1024;

const SAMPLE_THREADS = [
  {
    id: "thread-1",
    title: "Soft gradients for night skies",
    category: "Tips and tricks",
    sectionId: "tips-tricks",
    subsectionId: "techniques",
    author: "StitchBloom",
    handle: "@stitchbloom",
    replies: 12,
    views: 184,
    time: "20m ago",
    excerpt: "Share your favorite blend combos for smooth dusk transitions.",
  },
  {
    id: "thread-2",
    title: "Palette check: desert botanicals",
    category: "Color planning",
    sectionId: "color-planning",
    subsectionId: "palette-feedback",
    author: "ThreadWitch",
    handle: "@threadwitch",
    replies: 7,
    views: 96,
    time: "1h ago",
    excerpt: "Looking for a cooler accent color to balance these warm tones.",
  },
  {
    id: "thread-3",
    title: "Mini badge set for journals",
    category: "Showcase",
    sectionId: "showcase",
    subsectionId: "finished-pieces",
    author: "GridFox",
    handle: "@gridfox",
    replies: 4,
    views: 58,
    time: "3h ago",
    excerpt: "Three tiny icon ideas for a planner cover, feedback welcome.",
  },
  {
    id: "thread-4",
    title: "Backstitch questions on diagonal lines",
    category: "Pattern help",
    sectionId: "pattern-help",
    subsectionId: "symbol-clarity",
    author: "NeedleNova",
    handle: "@needlenova",
    replies: 9,
    views: 112,
    time: "6h ago",
    excerpt: "How do you keep diagonals crisp without over-thickening?",
  },
  {
    id: "thread-5",
    title: "Organizing floss for travel kits",
    category: "Supplies",
    sectionId: "supplies",
    subsectionId: "storage",
    author: "LoopLuna",
    handle: "@loopluna",
    replies: 5,
    views: 73,
    time: "10h ago",
    excerpt: "Share your best storage hacks for mobile stitching.",
  },
  {
    id: "thread-6",
    title: "Winter sampler: progress shots",
    category: "Work in progress",
    sectionId: "work-in-progress",
    subsectionId: "milestones",
    author: "NeedleNorth",
    handle: "@needlenorth",
    replies: 16,
    views: 220,
    time: "1d ago",
    excerpt: "Halfway through the border, loving the icy blues.",
  },
];

const SAMPLE_GALLERY = [
  {
    id: "gallery-1",
    designName: "Garden sampler",
    authorName: "ThreadBloom",
    authorHandle: "@threadbloom",
    cols: 80,
    rows: 90,
    paletteCount: 18,
    stitchCount: 6200,
    createdAt: new Date(),
  },
  {
    id: "gallery-2",
    designName: "Stargazer badge",
    authorName: "NeedleNova",
    authorHandle: "@needlenova",
    cols: 64,
    rows: 64,
    paletteCount: 12,
    stitchCount: 2300,
    createdAt: new Date(),
  },
  {
    id: "gallery-3",
    designName: "Botanical border",
    authorName: "PixelStitch",
    authorHandle: "@pixelstitch",
    cols: 120,
    rows: 60,
    paletteCount: 24,
    stitchCount: 5400,
    createdAt: new Date(),
  },
];

const TRENDING_TAGS = ["floral", "pixel", "sampler", "holiday", "pastels"];
const ROLE_OPTIONS = ["member", "moderator", "admin"];

const INLINE_TOKEN_REGEX =
  /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`|\*[^*]+\*|_[^_]+_|\[[^\]]+\]\([^)]+\))/g;

const isSafeLink = (value) => /^https?:\/\//i.test(value || "");

const renderInlineTokens = (text, keyPrefix) => {
  if (!text) return null;
  INLINE_TOKEN_REGEX.lastIndex = 0;
  const parts = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;
  const pushText = (value) => {
    if (value) parts.push(value);
  };

  while ((match = INLINE_TOKEN_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushText(text.slice(lastIndex, match.index));
    }
    const token = match[0];
    const makeKey = (suffix) => `${keyPrefix}-${keyIndex++}-${suffix}`;

    if (token.startsWith("**") && token.endsWith("**")) {
      parts.push(
        <strong key={makeKey("bold")}>{token.slice(2, -2)}</strong>
      );
    } else if (token.startsWith("__") && token.endsWith("__")) {
      parts.push(
        <strong key={makeKey("bold")}>{token.slice(2, -2)}</strong>
      );
    } else if (token.startsWith("`") && token.endsWith("`")) {
      parts.push(<code key={makeKey("code")}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("*") && token.endsWith("*")) {
      parts.push(
        <em key={makeKey("italic")}>{token.slice(1, -1)}</em>
      );
    } else if (token.startsWith("_") && token.endsWith("_")) {
      parts.push(
        <em key={makeKey("italic")}>{token.slice(1, -1)}</em>
      );
    } else if (token.startsWith("[")) {
      const linkMatch = token.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        const label = linkMatch[1];
        const url = linkMatch[2];
        if (isSafeLink(url)) {
          parts.push(
            <a
              key={makeKey("link")}
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              {label}
            </a>
          );
        } else {
          parts.push(label);
        }
      } else {
        parts.push(token);
      }
    } else {
      parts.push(token);
    }
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) {
    pushText(text.slice(lastIndex));
  }
  return parts;
};

const renderFormattedBlocks = (text, keyPrefix = "format") => {
  if (!text) return null;
  const lines = text.split("\n");
  const blocks = [];
  let listType = null;
  let listItems = [];
  let blockIndex = 0;

  const flushList = () => {
    if (!listItems.length) return;
    const listKey = `${keyPrefix}-list-${blockIndex++}`;
    const listContent = listItems.map((item, index) => (
      <li key={`${listKey}-item-${index}`}>
        {renderInlineTokens(item, `${listKey}-inline-${index}`)}
      </li>
    ));
    blocks.push(
      listType === "ol" ? (
        <ol key={listKey}>{listContent}</ol>
      ) : (
        <ul key={listKey}>{listContent}</ul>
      )
    );
    listItems = [];
    listType = null;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      blocks.push(<div key={`${keyPrefix}-spacer-${blockIndex++}`} />);
      return;
    }
    if (trimmed.startsWith("> ")) {
      flushList();
      blocks.push(
        <blockquote key={`${keyPrefix}-quote-${blockIndex++}`}>
          {renderInlineTokens(trimmed.slice(2), `${keyPrefix}-quote-inline`)}
        </blockquote>
      );
      return;
    }
    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      if (listType && listType !== "ol") flushList();
      listType = "ol";
      listItems.push(orderedMatch[1]);
      return;
    }
    const unorderedMatch = trimmed.match(/^[-*]\s+(.*)$/);
    if (unorderedMatch) {
      if (listType && listType !== "ul") flushList();
      listType = "ul";
      listItems.push(unorderedMatch[1]);
      return;
    }
    flushList();
    blocks.push(
      <p key={`${keyPrefix}-p-${blockIndex++}`}>
        {renderInlineTokens(line, `${keyPrefix}-p-inline-${blockIndex}`)}
      </p>
    );
  });

  flushList();
  return blocks;
};

const formatPostTime = (value) => {
  if (!value) return "just now";
  const date = value.toDate ? value.toDate() : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return "just now";
  return date.toLocaleDateString();
};

const formatCount = (value) => {
  if (!Number.isFinite(value)) return "0";
  return value.toLocaleString();
};

const truncateText = (text, maxLength = 140) => {
  if (!text) return "";
  const trimmed = text.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength - 3)}...`;
};

const getTimestampValue = (value) => {
  if (!value) return 0;
  const date = value.toDate ? value.toDate() : new Date(value);
  const time = date?.getTime?.();
  return Number.isFinite(time) ? time : 0;
};

export default function CommunityForum({
  user,
  communitySettings,
  currentDesignName,
  buildGalleryPayload,
}) {
  const canPost = Boolean(user);
  const role = (communitySettings?.role || "member").toLowerCase();
  const isAdmin = role === "admin";
  const canModerate = role === "admin" || role === "moderator";

  const [activeTab, setActiveTab] = useState("board");
  const [threads, setThreads] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsError, setThreadsError] = useState("");
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [activeThread, setActiveThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");

  const [postTitle, setPostTitle] = useState("");
  const [postBody, setPostBody] = useState("");
  const [postSectionId, setPostSectionId] = useState(SECTION_LIST[0]?.id || "");
  const [postSubsectionId, setPostSubsectionId] = useState(
    SECTION_LIST[0]?.subsections?.[0]?.id || "general"
  );
  const [postError, setPostError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [threadAttachments, setThreadAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState("");
  const [isBuildingAttachment, setIsBuildingAttachment] = useState(false);
  const [showPostPreview, setShowPostPreview] = useState(false);

  const [commentBody, setCommentBody] = useState("");
  const [commentError, setCommentError] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showReplyPreview, setShowReplyPreview] = useState(false);

  const [editingThread, setEditingThread] = useState(false);
  const [editingThreadTitle, setEditingThreadTitle] = useState("");
  const [editingThreadBody, setEditingThreadBody] = useState("");
  const [threadEditError, setThreadEditError] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentBody, setEditingCommentBody] = useState("");
  const [commentEditError, setCommentEditError] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [activeSectionId, setActiveSectionId] = useState("all");
  const [activeSubsectionId, setActiveSubsectionId] = useState("all");
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [activeGalleryAuthor, setActiveGalleryAuthor] = useState(null);

  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [shareTitle, setShareTitle] = useState("");
  const [shareDescription, setShareDescription] = useState("");
  const [shareError, setShareError] = useState("");
  const [isSharing, setIsSharing] = useState(false);

  const [forumSettings, setForumSettings] = useState(DEFAULT_FORUM_SETTINGS);
  const [settingsDraft, setSettingsDraft] = useState({
    forumName: DEFAULT_FORUM_SETTINGS.forumName,
    announcement: DEFAULT_FORUM_SETTINGS.announcement,
  });
  const [settingsStatus, setSettingsStatus] = useState("");
  const [settingsError, setSettingsError] = useState("");

  const [roleUpdateStatus, setRoleUpdateStatus] = useState("");
  const [roleUpdateError, setRoleUpdateError] = useState("");
  const [manualRoleUid, setManualRoleUid] = useState("");
  const [manualRole, setManualRole] = useState("member");
  const [profileRoles, setProfileRoles] = useState({});

  const refreshTimer = useRef(null);
  const attachmentInputRef = useRef(null);
  const postBodyRef = useRef(null);
  const commentBodyRef = useRef(null);

  const forumName =
    forumSettings.forumName?.trim() || DEFAULT_FORUM_SETTINGS.forumName;
  const allowGallery = forumSettings.allowGallery !== false;
  const allowImages = forumSettings.allowImages !== false;
  const allowDesignShares = forumSettings.allowDesignShares !== false;
  const lockNewThreads = forumSettings.lockNewThreads === true;
  const lockReplies = forumSettings.lockReplies === true;
  const activeSection = SECTION_LIST.find(
    (section) => section.id === activeSectionId
  );
  const activeSectionLabel =
    activeSectionId === "all" ? "All sections" : activeSection?.label || "Section";
  const activeSubsections = activeSection?.subsections || [];
  const activeSubsectionLabel =
    activeSectionId === "all" || activeSubsectionId === "all"
      ? "All topics"
      : activeSubsections.find((subsection) => subsection.id === activeSubsectionId)
          ?.label || "Topic";

  useEffect(() => () => clearTimeout(refreshTimer.current), []);

  useEffect(() => {
    setSettingsDraft({
      forumName: forumSettings.forumName || DEFAULT_FORUM_SETTINGS.forumName,
      announcement: forumSettings.announcement || "",
    });
  }, [forumSettings.forumName, forumSettings.announcement]);

  useEffect(() => {
    if (!user) return undefined;
    const settingsRef = doc(db, "community_settings", "forum");
    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        const data = snapshot.exists() ? snapshot.data() : {};
        setForumSettings({ ...DEFAULT_FORUM_SETTINGS, ...data });
        setSettingsError("");
      },
      (error) => {
        console.error("Failed to load forum settings:", error);
        setSettingsError("Unable to load forum settings.");
      }
    );
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!allowGallery && activeTab === "gallery" && !isAdmin) {
      setActiveTab("board");
    }
  }, [allowGallery, activeTab, isAdmin]);

  useEffect(() => {
    if (!user) {
      setThreads([]);
      setThreadsLoading(false);
      return undefined;
    }
    setThreadsLoading(true);
    setThreadsError("");
    const threadsRef = collection(db, "community_threads");
    const threadsQuery = query(threadsRef, orderBy("createdAt", "desc"), limit(80));
    const unsubscribe = onSnapshot(
      threadsQuery,
      (snapshot) => {
        const nextThreads = snapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }));
        setThreads(nextThreads);
        setThreadsLoading(false);
      },
      (error) => {
        console.error("Failed to load forum threads:", error);
        setThreadsError("Unable to load threads right now.");
        setThreadsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (activeTab !== "board") {
      setActiveThreadId(null);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!activeThreadId || !user) {
      setActiveThread(null);
      setComments([]);
      return undefined;
    }
    const threadRef = doc(db, "community_threads", activeThreadId);
    setCommentsLoading(true);
    setCommentsError("");
    const unsubscribeThread = onSnapshot(
      threadRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setActiveThread(null);
          setComments([]);
          return;
        }
        const data = snapshot.data() || {};
        if (data.isDeleted) {
          setActiveThread(null);
          setComments([]);
          return;
        }
        setActiveThread({ id: snapshot.id, ...data });
      },
      (error) => {
        console.error("Failed to load thread:", error);
        setCommentsError("Unable to load this thread.");
      }
    );
    const commentsRef = collection(threadRef, "comments");
    const commentsQuery = query(commentsRef, orderBy("createdAt", "asc"), limit(200));
    const unsubscribeComments = onSnapshot(
      commentsQuery,
      (snapshot) => {
        const nextComments = snapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }));
        setComments(nextComments);
        setCommentsLoading(false);
      },
      (error) => {
        console.error("Failed to load comments:", error);
        setCommentsError("Unable to load comments right now.");
        setCommentsLoading(false);
      }
    );
    return () => {
      unsubscribeThread();
      unsubscribeComments();
    };
  }, [activeThreadId, user]);

  useEffect(() => {
    if (!activeThread) return;
    if (!editingThread) {
      setEditingThreadTitle(activeThread.title || "");
      setEditingThreadBody(activeThread.body || "");
    }
  }, [activeThread, editingThread]);

  useEffect(() => {
    setCommentError("");
    setCommentBody("");
  }, [activeThreadId]);

  useEffect(() => {
    if (!user) {
      setGalleryItems([]);
      setGalleryLoading(false);
      return undefined;
    }
    setGalleryLoading(true);
    setGalleryError("");
    const galleryRef = collection(db, "community_gallery");
    const galleryQuery = query(galleryRef, orderBy("createdAt", "desc"), limit(60));
    const unsubscribe = onSnapshot(
      galleryQuery,
      (snapshot) => {
        const nextGallery = snapshot.docs.map((docSnapshot) => ({
          id: docSnapshot.id,
          ...docSnapshot.data(),
        }));
        setGalleryItems(nextGallery);
        setGalleryLoading(false);
      },
      (error) => {
        console.error("Failed to load gallery:", error);
        setGalleryError("Unable to load gallery right now.");
        setGalleryLoading(false);
      }
    );
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const uniqueMembers = Array.from(
      new Map(
        threads
          .filter((thread) => thread?.authorId)
          .map((thread) => [
            thread.authorId,
            {
              id: thread.authorId,
              name: thread.authorName || "Community member",
              handle: thread.authorHandle ? `@${thread.authorHandle}` : "",
            },
          ])
      ).values()
    ).slice(0, 8);
    if (uniqueMembers.length === 0) return undefined;
    let cancelled = false;
    const loadRoles = async () => {
      try {
        const results = await Promise.all(
          uniqueMembers.map(async (member) => {
            try {
              const snap = await getDoc(doc(db, "community_profiles", member.id));
              return snap.exists()
                ? { id: member.id, role: snap.data()?.role || "member" }
                : { id: member.id, role: "member" };
            } catch (error) {
              return { id: member.id, role: "member" };
            }
          })
        );
        if (cancelled) return;
        const nextRoles = results.reduce((acc, entry) => {
          acc[entry.id] = entry.role;
          return acc;
        }, {});
        setProfileRoles((prev) => ({ ...prev, ...nextRoles }));
      } catch (error) {
        if (!cancelled) {
          console.warn("Unable to load member roles:", error);
        }
      }
    };
    loadRoles();
    return () => {
      cancelled = true;
    };
  }, [isAdmin, threads]);

  useEffect(() => {
    if (!currentDesignName || shareTitle.trim()) return;
    setShareTitle(currentDesignName);
  }, [currentDesignName, shareTitle]);

  useEffect(() => {
    if (activeSectionId !== "all") {
      setPostSectionId(activeSectionId);
    }
    setActiveSubsectionId("all");
  }, [activeSectionId]);

  useEffect(() => {
    const section = SECTION_LIST.find((item) => item.id === postSectionId);
    const fallback = section?.subsections?.[0]?.id || "general";
    setPostSubsectionId((prev) => {
      if (!section) return fallback;
      return section.subsections?.some((sub) => sub.id === prev) ? prev : fallback;
    });
  }, [postSectionId]);

  const threadItems = useMemo(() => {
    if (!user && threads.length === 0) {
      return SAMPLE_THREADS.map((thread) => ({
        id: thread.id,
        title: thread.title,
        sectionLabel: thread.category || "General",
        sectionId: thread.sectionId || "",
        subsectionId: thread.subsectionId || "general",
        subsectionLabel:
          SECTION_LIST.find((section) => section.id === thread.sectionId)
            ?.subsections?.find((sub) => sub.id === thread.subsectionId)?.label ||
          "General chat",
        author: thread.author,
        handle: thread.handle,
        replies: thread.replies,
        views: thread.views,
        time: thread.time,
        excerpt: thread.excerpt,
        isPinned: false,
        isLocked: false,
        isDeleted: false,
        attachments: [],
      }));
    }
    const normalizedSearch = searchQuery.trim().toLowerCase();
    const activeSection = SECTION_LIST.find(
      (section) => section.id === activeSectionId
    );
    const activeSectionLabel = activeSection?.label;
    const sorted = [...threads].sort((a, b) => {
      const pinDiff = (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0);
      if (pinDiff !== 0) return pinDiff;
      const timeA = getTimestampValue(a.updatedAt || a.createdAt);
      const timeB = getTimestampValue(b.updatedAt || b.createdAt);
      return timeB - timeA;
    });
    return sorted
      .filter((thread) =>
        activeSectionId === "all"
          ? true
          : thread.sectionId
            ? thread.sectionId === activeSectionId
            : thread.category === activeSectionLabel
      )
      .filter((thread) => {
        if (activeSectionId === "all" || activeSubsectionId === "all") {
          return true;
        }
        const subsectionId = thread.subsectionId || "general";
        return subsectionId === activeSubsectionId;
      })
      .filter((thread) => !thread.isDeleted)
      .filter((thread) => {
        if (!normalizedSearch) return true;
        const title = (thread.title || "").toLowerCase();
        const body = (thread.body || "").toLowerCase();
        const author = (thread.authorName || "").toLowerCase();
        return (
          title.includes(normalizedSearch) ||
          body.includes(normalizedSearch) ||
          author.includes(normalizedSearch)
        );
      })
      .map((thread) => {
        const section =
          SECTION_LIST.find((section) => section.id === thread.sectionId) ||
          null;
        const subsection =
          section?.subsections?.find((sub) => sub.id === thread.subsectionId) ||
          null;
        return {
        id: thread.id,
        title: thread.title || "Untitled thread",
        sectionLabel: thread.sectionLabel || thread.category || "General",
        sectionId: thread.sectionId || "",
        subsectionId: thread.subsectionId || "general",
        subsectionLabel: thread.subsectionLabel || subsection?.label || "General chat",
        author: thread.authorName || "Community member",
        handle: thread.authorHandle ? `@${thread.authorHandle}` : "",
        replies: thread.replyCount || 0,
        views: thread.viewCount || 0,
        time: formatPostTime(thread.updatedAt || thread.createdAt),
        excerpt: truncateText(thread.body || "", 180),
        isPinned: !!thread.isPinned,
        isLocked: !!thread.isLocked,
        isDeleted: !!thread.isDeleted,
        attachments: Array.isArray(thread.attachments) ? thread.attachments : [],
        };
      });
  }, [threads, user, activeSectionId, activeSubsectionId, activeSectionLabel, searchQuery]);

  const galleryFeed = useMemo(() => {
    if (!user && galleryItems.length === 0) {
      return SAMPLE_GALLERY;
    }
    return galleryItems;
  }, [galleryItems, user]);

  const filteredGalleryFeed = useMemo(() => {
    if (!activeGalleryAuthor) return galleryFeed;
    return galleryFeed.filter((item) => {
      if (activeGalleryAuthor.id) {
        return item.authorId === activeGalleryAuthor.id;
      }
      if (activeGalleryAuthor.handle) {
        return (item.authorHandle || "").toLowerCase() === activeGalleryAuthor.handle;
      }
      return (item.authorName || "").toLowerCase() === activeGalleryAuthor.name;
    });
  }, [galleryFeed, activeGalleryAuthor]);

  const memberDirectory = useMemo(() => {
    const map = new Map();
    const addMember = ({ id, name, handle, source }) => {
      if (!name && !handle) return;
      const key = id || handle || name;
      if (!key) return;
      const entry = map.get(key) || {
        id: id || "",
        name: name || "Community member",
        handle: handle ? `@${handle.replace(/^@/, "")}` : "",
        sources: new Set(),
        galleryCount: 0,
        threadCount: 0,
      };
      if (source === "gallery") entry.galleryCount += 1;
      if (source === "thread") entry.threadCount += 1;
      if (source) entry.sources.add(source);
      if (!entry.id && id) entry.id = id;
      if (!entry.handle && handle) entry.handle = `@${handle.replace(/^@/, "")}`;
      if (!entry.name && name) entry.name = name;
      map.set(key, entry);
    };

    galleryItems.forEach((item) => {
      addMember({
        id: item.authorId || "",
        name: item.authorName || "",
        handle: item.authorHandle || "",
        source: "gallery",
      });
    });
    threads.forEach((thread) => {
      addMember({
        id: thread.authorId || "",
        name: thread.authorName || "",
        handle: thread.authorHandle || "",
        source: "thread",
      });
    });

    return Array.from(map.values())
      .map((entry) => ({
        ...entry,
        nameLower: (entry.name || "").toLowerCase(),
        handleLower: (entry.handle || "").toLowerCase(),
      }))
      .sort(
        (a, b) =>
          b.galleryCount - a.galleryCount ||
          b.threadCount - a.threadCount ||
          a.name.localeCompare(b.name)
      );
  }, [galleryItems, threads]);

  const memberMatches = useMemo(() => {
    const query = memberSearchQuery.trim().toLowerCase();
    if (!query) return [];
    return memberDirectory
      .filter(
        (member) =>
          member.nameLower.includes(query) ||
          member.handleLower.includes(query)
      )
      .slice(0, 6);
  }, [memberDirectory, memberSearchQuery]);

  const uniqueMembers = useMemo(() => {
    return Array.from(
      new Map(
        threads
          .filter((thread) => thread?.authorId)
          .map((thread) => [
            thread.authorId,
            {
              id: thread.authorId,
              name: thread.authorName || "Community member",
              handle: thread.authorHandle ? `@${thread.authorHandle}` : "",
            },
          ])
      ).values()
    ).slice(0, 8);
  }, [threads]);

  const buildAttachmentId = () =>
    `att-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

  const getDataUrlByteSize = (dataUrl) => {
    if (!dataUrl || typeof dataUrl !== "string") return 0;
    const base64 = dataUrl.split(",")[1] || "";
    return Math.floor((base64.length * 3) / 4);
  };

  const validateAttachmentSize = (previewUrl) => {
    const size = getDataUrlByteSize(previewUrl);
    if (size > MAX_ATTACHMENT_DATA_BYTES) {
      setAttachmentError("Attachment preview is too large. Try a smaller or cropped image.");
      return false;
    }
    return true;
  };

  const handleForumToggle = async (key, value) => {
    if (!isAdmin) return;
    setForumSettings((prev) => ({ ...prev, [key]: value }));
    setSettingsError("");
    try {
      await setDoc(
        doc(db, "community_settings", "forum"),
        { [key]: value, updatedAt: serverTimestamp() },
        { merge: true }
      );
      setSettingsStatus("Saved.");
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => setSettingsStatus(""), 2000);
    } catch (error) {
      console.error("Failed to update forum settings:", error);
      setSettingsError("Unable to save forum settings.");
    }
  };

  const handleSettingsSave = async () => {
    if (!isAdmin) return;
    setSettingsError("");
    const forumNameValue =
      settingsDraft.forumName?.trim() || DEFAULT_FORUM_SETTINGS.forumName;
    const announcementValue = settingsDraft.announcement?.trim() || "";
    try {
      await setDoc(
        doc(db, "community_settings", "forum"),
        {
          forumName: forumNameValue,
          announcement: announcementValue,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      setSettingsStatus("Settings saved.");
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => setSettingsStatus(""), 2000);
    } catch (error) {
      console.error("Failed to save forum settings:", error);
      setSettingsError("Unable to save forum settings.");
    }
  };

  const handleSectionSelect = (sectionId) => {
    setActiveSectionId(sectionId);
    setActiveSubsectionId("all");
  };

  const handleSubsectionSelect = (subsectionId) => {
    setActiveSubsectionId(subsectionId);
  };

  const applyInlineFormat = (ref, setter, { prefix, suffix = "", placeholder }) => {
    const field = ref.current;
    if (!field) return;
    const value = field.value || "";
    const start = field.selectionStart ?? 0;
    const end = field.selectionEnd ?? start;
    const selected = value.slice(start, end) || placeholder || "";
    const next = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      node.focus();
      const cursorStart = start + prefix.length;
      const cursorEnd = cursorStart + selected.length;
      node.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const applyListFormat = (ref, setter, listType) => {
    const field = ref.current;
    if (!field) return;
    const value = field.value || "";
    const start = field.selectionStart ?? 0;
    const end = field.selectionEnd ?? start;
    const selected = value.slice(start, end) || "List item";
    const lines = selected.split("\n");
    const formatted = lines.map((line, index) => {
      const cleaned = line.replace(/^(\s*([-*]|\d+\.)\s+)/, "");
      if (listType === "ordered") {
        return `${index + 1}. ${cleaned}`;
      }
      return `- ${cleaned}`;
    });
    const next = value.slice(0, start) + formatted.join("\n") + value.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      node.focus();
      const cursorStart = start;
      const cursorEnd = start + formatted.join("\n").length;
      node.setSelectionRange(cursorStart, cursorEnd);
    });
  };

  const applyQuoteFormat = (ref, setter) => {
    const field = ref.current;
    if (!field) return;
    const value = field.value || "";
    const start = field.selectionStart ?? 0;
    const end = field.selectionEnd ?? start;
    const selected = value.slice(start, end) || "Quote";
    const formatted = selected
      .split("\n")
      .map((line) => `> ${line.replace(/^>\s?/, "")}`)
      .join("\n");
    const next = value.slice(0, start) + formatted + value.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(start, start + formatted.length);
    });
  };

  const applyCodeBlockFormat = (ref, setter) => {
    applyInlineFormat(ref, setter, {
      prefix: "```\n",
      suffix: "\n```",
      placeholder: "code",
    });
  };

  const applyLinkFormat = (ref, setter) => {
    const field = ref.current;
    if (!field) return;
    const value = field.value || "";
    const start = field.selectionStart ?? 0;
    const end = field.selectionEnd ?? start;
    const selected = value.slice(start, end) || "link text";
    const link = `[${selected}](https://)`;
    const next = value.slice(0, start) + link + value.slice(end);
    setter(next);
    requestAnimationFrame(() => {
      const node = ref.current;
      if (!node) return;
      node.focus();
      const urlStart = start + link.indexOf("https://");
      const urlEnd = urlStart + "https://".length;
      node.setSelectionRange(urlStart, urlEnd);
    });
  };

  const handleMemberSelect = (member) => {
    if (!member) return;
    setActiveGalleryAuthor({
      id: member.id || "",
      name: member.name?.toLowerCase() || "",
      handle: member.handle?.toLowerCase().replace(/^@/, "") || "",
      label: member.name || "Community member",
      handleLabel: member.handle || "",
    });
    setMemberSearchQuery("");
    setActiveTab("gallery");
  };

  const clearGalleryFilter = () => {
    setActiveGalleryAuthor(null);
  };

  const addAttachment = (attachment) => {
    setThreadAttachments((prev) => {
      if (prev.length >= ATTACHMENT_LIMIT) {
        return prev;
      }
      return [...prev, attachment];
    });
  };

  const handleRemoveAttachment = (attachmentId) => {
    setThreadAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== attachmentId)
    );
  };

  const handleAddDesignAttachment = async () => {
    if (lockNewThreads) {
      setAttachmentError("New threads are paused by admins.");
      return;
    }
    if (!allowDesignShares) {
      setAttachmentError("Design sharing is disabled by admin.");
      return;
    }
    if (!buildGalleryPayload) {
      setAttachmentError("Design capture is unavailable.");
      return;
    }
    if (threadAttachments.length >= ATTACHMENT_LIMIT) {
      setAttachmentError("Attachment limit reached.");
      return;
    }
    setIsBuildingAttachment(true);
    setAttachmentError("");
    try {
      const payload = await buildGalleryPayload();
      if (!payload?.previewUrl) {
        throw new Error("Preview could not be created.");
      }
      if (validateAttachmentSize(payload.previewUrl)) {
        addAttachment({
          id: buildAttachmentId(),
          type: "design",
          title: postTitle.trim() || currentDesignName || "Untitled design",
          previewUrl: payload.previewUrl,
          cols: payload.cols,
          rows: payload.rows,
          paletteCount: payload.paletteCount,
          stitchCount: payload.stitchCount,
          isPixelMode: payload.isPixelMode,
          aidaCount: payload.aidaCount,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Failed to add design attachment:", error);
      setAttachmentError("Unable to attach design.");
    } finally {
      setIsBuildingAttachment(false);
    }
  };

  const createImagePreview = (file) =>
    new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const maxDimension = Math.max(image.width, image.height, 1);
        const scale = Math.min(1, ATTACHMENT_PREVIEW_SIZE / maxDimension);
        const width = Math.max(1, Math.round(image.width * scale));
        const height = Math.max(1, Math.round(image.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas unavailable."));
          return;
        }
        ctx.drawImage(image, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error("Image load failed."));
      };
      image.src = objectUrl;
    });

  const handleAttachmentUploadClick = () => {
    if (attachmentInputRef.current) {
      attachmentInputRef.current.click();
    }
  };

  const handleAttachmentFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (lockNewThreads) {
      setAttachmentError("New threads are paused by admins.");
      return;
    }
    if (!allowImages) {
      setAttachmentError("Image uploads are disabled by admin.");
      return;
    }
    if (threadAttachments.length >= ATTACHMENT_LIMIT) {
      setAttachmentError("Attachment limit reached.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      setAttachmentError("Image is too large to process. Try a smaller file.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setAttachmentError("Only image files are supported.");
      return;
    }
    setIsBuildingAttachment(true);
    setAttachmentError("");
    try {
      const previewUrl = await createImagePreview(file);
      if (validateAttachmentSize(previewUrl)) {
        addAttachment({
          id: buildAttachmentId(),
          type: "image",
          title: file.name || "Image upload",
          previewUrl,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Failed to add image attachment:", error);
      setAttachmentError("Unable to attach image. Try a JPG or PNG.");
    } finally {
      setIsBuildingAttachment(false);
    }
  };

  const handlePostSubmit = async () => {
    if (!canPost || isSubmitting) return;
    if (lockNewThreads) {
      setPostError("New threads are paused by admins.");
      return;
    }
    const title = postTitle.trim();
    const body = postBody.trim();
    if (!title || !body) {
      setPostError("Add a title and message before posting.");
      return;
    }
    if (isBuildingAttachment) {
      setPostError("Please wait for attachments to finish uploading.");
      return;
    }
    const attachmentBytes = threadAttachments.reduce((sum, attachment) => {
      return sum + getDataUrlByteSize(attachment.previewUrl);
    }, 0);
    if (attachmentBytes > MAX_THREAD_ATTACHMENT_BYTES) {
      setPostError("Attachments are too large for a single post.");
      return;
    }

    setIsSubmitting(true);
    setPostError("");

    try {
      const authorName = (communitySettings?.displayName || user?.displayName || "Anonymous").trim();
      const authorHandle = (communitySettings?.handle || "").trim();
      const authorRole = role || "member";
      const selectedSection =
        SECTION_LIST.find((section) => section.id === postSectionId) ||
        SECTION_LIST[0];
      const sectionLabel = selectedSection?.label || "General";
      const selectedSubsection =
        selectedSection?.subsections?.find(
          (subsection) => subsection.id === postSubsectionId
        ) || selectedSection?.subsections?.[0] || {
          id: "general",
          label: "General chat",
        };

      await addDoc(collection(db, "community_threads"), {
        title,
        body,
        category: sectionLabel,
        sectionId: selectedSection?.id || "",
        sectionLabel,
        subsectionId: selectedSubsection.id || "general",
        subsectionLabel: selectedSubsection.label || "General chat",
        authorId: user.uid,
        authorName,
        authorHandle,
        authorRole,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        replyCount: 0,
        viewCount: 0,
        isPinned: false,
        isLocked: false,
        isDeleted: false,
        attachments: threadAttachments,
      });

      setPostTitle("");
      setPostBody("");
      setPostSectionId(SECTION_LIST[0]?.id || "");
      setPostSubsectionId(SECTION_LIST[0]?.subsections?.[0]?.id || "general");
      setThreadAttachments([]);
      setAttachmentError("");
    } catch (error) {
      console.error("Failed to create thread:", error);
      setPostError(error?.message || "Post failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenThread = (threadId) => {
    if (!user) return;
    setActiveThreadId(threadId);
    setEditingThread(false);
    setThreadEditError("");
  };

  const handleCloseThread = () => {
    setActiveThreadId(null);
    setEditingThread(false);
    setEditingCommentId(null);
    setThreadEditError("");
    setCommentEditError("");
  };

  const handleThreadUpdate = async () => {
    if (!activeThread || !user) return;
    const title = editingThreadTitle.trim();
    const body = editingThreadBody.trim();
    if (!title || !body) {
      setThreadEditError("Title and body are required.");
      return;
    }
    try {
      const threadRef = doc(db, "community_threads", activeThread.id);
      await updateDoc(threadRef, {
        title,
        body,
        updatedAt: serverTimestamp(),
      });
      setEditingThread(false);
      setThreadEditError("");
    } catch (error) {
      console.error("Failed to update thread:", error);
      setThreadEditError("Unable to save changes.");
    }
  };

  const handleThreadModeration = async (updates) => {
    if (!activeThread) return;
    try {
      const threadRef = doc(db, "community_threads", activeThread.id);
      await updateDoc(threadRef, { ...updates, updatedAt: serverTimestamp() });
    } catch (error) {
      console.error("Failed to update thread:", error);
    }
  };

  const handleThreadRemove = async () => {
    if (!activeThread || !user) return;
    try {
      const threadRef = doc(db, "community_threads", activeThread.id);
      await deleteDoc(threadRef);
      handleCloseThread();
    } catch (error) {
      console.error("Failed to remove thread:", error);
    }
  };

  const handleCommentSubmit = async () => {
    if (!canPost || isSubmittingComment || !activeThread) return;
    if (lockReplies) {
      setCommentError("Replies are paused by admins.");
      return;
    }
    if (activeThread.isLocked || activeThread.isDeleted) {
      setCommentError("This thread is locked.");
      return;
    }
    const body = commentBody.trim();
    if (!body) {
      setCommentError("Write a reply before posting.");
      return;
    }
    setIsSubmittingComment(true);
    setCommentError("");
    try {
      const authorName = (communitySettings?.displayName || user?.displayName || "Anonymous").trim();
      const authorHandle = (communitySettings?.handle || "").trim();
      const authorRole = role || "member";
      const threadRef = doc(db, "community_threads", activeThread.id);
      await addDoc(collection(threadRef, "comments"), {
        body,
        authorId: user.uid,
        authorName,
        authorHandle,
        authorRole,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isEdited: false,
        isDeleted: false,
      });
      await updateDoc(threadRef, {
        replyCount: increment(1),
        lastReplyAt: serverTimestamp(),
        lastReplyBy: user.uid,
        updatedAt: serverTimestamp(),
      });
      setCommentBody("");
    } catch (error) {
      console.error("Failed to add comment:", error);
      setCommentError("Reply failed. Please try again.");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleCommentEdit = async (commentId) => {
    if (!activeThread || !commentId) return;
    const body = editingCommentBody.trim();
    if (!body) {
      setCommentEditError("Reply cannot be empty.");
      return;
    }
    try {
      const commentRef = doc(db, "community_threads", activeThread.id, "comments", commentId);
      await updateDoc(commentRef, {
        body,
        updatedAt: serverTimestamp(),
        isEdited: true,
      });
      setEditingCommentId(null);
      setEditingCommentBody("");
      setCommentEditError("");
    } catch (error) {
      console.error("Failed to edit comment:", error);
      setCommentEditError("Unable to save changes.");
    }
  };

  const handleCommentRemove = async (commentId) => {
    if (!activeThread || !commentId || !user) return;
    try {
      const commentRef = doc(db, "community_threads", activeThread.id, "comments", commentId);
      await updateDoc(commentRef, {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: user.uid,
      });
    } catch (error) {
      console.error("Failed to remove comment:", error);
    }
  };

  const handleShareDesign = async () => {
    if (!user || isSharing) return;
    if (!allowGallery) {
      setShareError("Gallery sharing is disabled by admins.");
      return;
    }
    if (!allowDesignShares) {
      setShareError("Design sharing is disabled by admins.");
      return;
    }
    const title = shareTitle.trim() || currentDesignName || "Untitled design";
    setShareError("");
    setIsSharing(true);
    try {
      if (typeof buildGalleryPayload !== "function") {
        throw new Error("Gallery payload builder is unavailable.");
      }
      const payload = await buildGalleryPayload();
      if (!payload?.previewUrl) {
        throw new Error("Preview could not be created.");
      }
      const authorName = (communitySettings?.displayName || user?.displayName || "Anonymous").trim();
      const authorHandle = (communitySettings?.handle || "").trim();
      await addDoc(collection(db, "community_gallery"), {
        designName: title,
        description: shareDescription.trim(),
        authorId: user.uid,
        authorName,
        authorHandle,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...payload,
      });
      setShareDescription("");
      setShareTitle("");
    } catch (error) {
      console.error("Failed to share design:", error);
      setShareError(error?.message || "Unable to share design right now.");
    } finally {
      setIsSharing(false);
    }
  };

  const handleUnshareDesign = async (designId) => {
    if (!designId) return;
    try {
      await deleteDoc(doc(db, "community_gallery", designId));
    } catch (error) {
      console.error("Failed to remove design:", error);
    }
  };

  const handleRoleUpdate = async (uid, nextRole) => {
    if (!uid || !nextRole) return;
    setRoleUpdateStatus("");
    setRoleUpdateError("");
    try {
      await updateDoc(doc(db, "community_profiles", uid), {
        role: nextRole,
      });
      setProfileRoles((prev) => ({ ...prev, [uid]: nextRole }));
      setRoleUpdateStatus("Role updated.");
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => setRoleUpdateStatus(""), 2400);
    } catch (error) {
      console.error("Failed to update role:", error);
      setRoleUpdateError("Role update failed. Check the user id.");
    }
  };

  const handleManualRoleSubmit = async () => {
    const uid = manualRoleUid.trim();
    if (!uid) {
      setRoleUpdateError("Enter a user id.");
      return;
    }
    await handleRoleUpdate(uid, manualRole);
    setManualRoleUid("");
  };

  const isThreadOwner = activeThread && user?.uid && activeThread.authorId === user.uid;
  const canEditThread = isThreadOwner && !activeThread?.isLocked && !activeThread?.isDeleted;
  const canRemoveThread = (isThreadOwner || canModerate) && !activeThread?.isDeleted;
  const settingsRows = [
    {
      key: "lockNewThreads",
      label: "Lock new threads",
      description: "Pause new thread creation across all sections.",
      value: lockNewThreads,
    },
    {
      key: "lockReplies",
      label: "Lock replies",
      description: "Pause replies across the forum.",
      value: lockReplies,
    },
    {
      key: "allowImages",
      label: "Allow image uploads",
      description: "Permit JPG/PNG uploads on new threads.",
      value: allowImages,
    },
    {
      key: "allowDesignShares",
      label: "Allow design shares",
      description: "Let members attach designs from the app.",
      value: allowDesignShares,
    },
    {
      key: "allowGallery",
      label: "Enable gallery",
      description: "Show the community gallery tab and sharing.",
      value: allowGallery,
    },
  ];

  return (
    <section className="community-forum">
      <nav className="community-forum__nav">
        <div className="community-forum__nav-brand">
          <span className="community-forum__nav-title">{forumName}</span>
          <span className="community-forum__nav-subtitle">
            Community bulletin board
          </span>
        </div>
        <div className="community-forum__nav-links">
          <button
            className={`btn ${activeTab === "board" ? "btn-primary" : ""}`}
            type="button"
            onClick={() => setActiveTab("board")}
          >
            Board
          </button>
          {(allowGallery || isAdmin) && (
            <button
              className={`btn ${activeTab === "gallery" ? "btn-primary" : ""}`}
              type="button"
              onClick={() => setActiveTab("gallery")}
              disabled={!allowGallery && !isAdmin}
              title={
                !allowGallery && !isAdmin
                  ? "Gallery is disabled by admins."
                  : ""
              }
            >
              Gallery
            </button>
          )}
          {isAdmin && (
            <>
              <button
                className={`btn ${activeTab === "moderation" ? "btn-primary" : ""}`}
                type="button"
                onClick={() => setActiveTab("moderation")}
              >
                Moderation
              </button>
              <button
                className={`btn ${activeTab === "settings" ? "btn-primary" : ""}`}
                type="button"
                onClick={() => setActiveTab("settings")}
              >
                Settings
              </button>
            </>
          )}
        </div>
        <div className="community-forum__nav-search">
          <input
            className="input"
            type="search"
            placeholder="Search threads"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            disabled={activeTab !== "board" || Boolean(activeThreadId)}
          />
        </div>
        <div className="community-forum__nav-user-search">
          <input
            className="input"
            type="search"
            placeholder="Search members"
            value={memberSearchQuery}
            onChange={(event) => setMemberSearchQuery(event.target.value)}
            disabled={!user}
          />
          {memberSearchQuery.trim() && (
            <div className="community-forum__user-results">
              {memberMatches.length === 0 ? (
                <div className="community-forum__user-empty">
                  No matching members yet.
                </div>
              ) : (
                memberMatches.map((member) => (
                  <button
                    key={`${member.id || member.handle}-${member.name}`}
                    className="community-forum__user-result"
                    type="button"
                    onClick={() => handleMemberSelect(member)}
                  >
                    <span>
                      <strong>{member.name}</strong>{" "}
                      {member.handle && (
                        <span className="community-forum__muted">
                          {member.handle}
                        </span>
                      )}
                    </span>
                    <span className="community-forum__muted">
                      {member.galleryCount} gallery · {member.threadCount} threads
                    </span>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </nav>
      <div className="community-forum__hero">
        <div>
          <p className="community-forum__eyebrow">Community forum</p>
          <h2 className="community-forum__headline">
            Share ideas, ask questions, and inspire the next stitch.
          </h2>
          <p className="community-forum__subhead">
            This space is built for pattern feedback, WIP journals, and color
            planning discussions.
          </p>
        </div>
        <div className="community-forum__hero-card">
          <div>
            <span className="community-forum__stat-label">Active threads</span>
            <strong>{formatCount(threads.length || SAMPLE_THREADS.length)}</strong>
          </div>
          <div>
            <span className="community-forum__stat-label">Gallery posts</span>
            <strong>{formatCount(galleryFeed.length || SAMPLE_GALLERY.length)}</strong>
          </div>
          <div>
            <span className="community-forum__stat-label">Current role</span>
            <strong>{role}</strong>
          </div>
        </div>
      </div>

      {isAdmin && (
        <div className="community-forum__role-banner">
          <div>
            <strong>Admin access enabled</strong>
            <p>
              You can manage roles, pin or lock threads, and moderate replies.
            </p>
          </div>
          <span className="community-forum__pill is-pin">Admin</span>
        </div>
      )}

      {forumSettings.announcement?.trim() && (
        <div className="community-forum__announcement">
          <strong>Board notice</strong>
          <p>{forumSettings.announcement}</p>
        </div>
      )}

      {activeTab === "board" && !activeThreadId && (
        <>
          <div className="community-forum__section-nav">
            {SECTION_FILTERS.map((section) => (
              <button
                key={section.id}
                type="button"
                className={`community-forum__section-link${
                  activeSectionId === section.id ? " is-active" : ""
                }`}
                onClick={() => handleSectionSelect(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>
          {activeSectionId !== "all" && (
            <div className="community-forum__subsection-nav">
              {[SUBSECTION_FILTER_ALL, ...activeSubsections].map((subsection) => (
                <button
                  key={subsection.id}
                  type="button"
                  className={`community-forum__subsection-link${
                    activeSubsectionId === subsection.id ? " is-active" : ""
                  }`}
                  onClick={() => handleSubsectionSelect(subsection.id)}
                >
                  {subsection.label}
                </button>
              ))}
            </div>
          )}
          <div className="community-forum__layout">
          <aside className="community-forum__sidebar">
            <div className="community-forum__panel">
              <h4>Discussion groups</h4>
              <div className="community-forum__category-list">
                {SECTION_FILTERS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    className={`community-forum__category-btn${
                      activeSectionId === section.id ? " is-active" : ""
                    }`}
                    onClick={() => handleSectionSelect(section.id)}
                  >
                    <span>{section.label}</span>
                    {section.description && (
                      <span className="community-forum__category-desc">
                        {section.description}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="community-forum__panel">
              <h4>Guidelines</h4>
              <p>
                Be constructive, credit inspiration, and keep feedback kind.
              </p>
            </div>
          </aside>

          <main className="community-forum__main">
            <div className="community-forum__composer">
              <h4>Start a thread</h4>
              <input
                className="input"
                type="text"
                placeholder="Title your thread"
                value={postTitle}
                onChange={(event) => setPostTitle(event.target.value)}
                disabled={!canPost}
              />
              <label className="community-forum__select">
                <span>Section</span>
                <select
                  className="input"
                  value={postSectionId}
                  onChange={(event) => setPostSectionId(event.target.value)}
                  disabled={!canPost || lockNewThreads}
                >
                  {SECTION_LIST.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="community-forum__select">
                <span>Topic</span>
                <select
                  className="input"
                  value={postSubsectionId}
                  onChange={(event) => setPostSubsectionId(event.target.value)}
                  disabled={!canPost || lockNewThreads}
                >
                  {(SECTION_LIST.find((section) => section.id === postSectionId)
                    ?.subsections || []
                  ).map((subsection) => (
                    <option key={subsection.id} value={subsection.id}>
                      {subsection.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="community-forum__composer-tools">
                <span className="community-forum__muted">Formatting</span>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() =>
                    applyInlineFormat(postBodyRef, setPostBody, {
                      prefix: "**",
                      suffix: "**",
                      placeholder: "bold text",
                    })
                  }
                >
                  Bold
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() =>
                    applyInlineFormat(postBodyRef, setPostBody, {
                      prefix: "*",
                      suffix: "*",
                      placeholder: "italic text",
                    })
                  }
                >
                  Italic
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() => applyListFormat(postBodyRef, setPostBody, "unordered")}
                >
                  Bullets
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() => applyListFormat(postBodyRef, setPostBody, "ordered")}
                >
                  Numbered
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() => applyQuoteFormat(postBodyRef, setPostBody)}
                >
                  Quote
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() => applyCodeBlockFormat(postBodyRef, setPostBody)}
                >
                  Code
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() => applyLinkFormat(postBodyRef, setPostBody)}
                >
                  Link
                </button>
                <button
                  className="btn"
                  type="button"
                  disabled={!canPost || lockNewThreads}
                  onClick={() => setShowPostPreview((value) => !value)}
                >
                  {showPostPreview ? "Hide preview" : "Preview"}
                </button>
              </div>
              <textarea
                className="input"
                rows={4}
                placeholder="Share your idea or question"
                value={postBody}
                onChange={(event) => setPostBody(event.target.value)}
                disabled={!canPost || lockNewThreads}
                ref={postBodyRef}
              />
              {showPostPreview && (
                <div className="community-forum__format-preview community-forum__formatted-blocks">
                  {renderFormattedBlocks(postBody, "post-preview") || (
                    <span className="community-forum__muted">
                      Start typing to preview your formatting.
                    </span>
                  )}
                </div>
              )}
              <div className="community-forum__attachment-block">
                <input
                  ref={attachmentInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAttachmentFileChange}
                  style={{ display: "none" }}
                />
                <div className="community-forum__attachment-actions">
                  <button
                    className="btn"
                    type="button"
                    onClick={handleAddDesignAttachment}
                    disabled={
                      !canPost ||
                      isBuildingAttachment ||
                      !allowDesignShares ||
                      lockNewThreads
                    }
                    title={
                      !allowDesignShares
                        ? "Design sharing is disabled by admins."
                        : "Attach the design currently open in the app."
                    }
                  >
                    Attach current design
                  </button>
                  <button
                    className="btn"
                    type="button"
                    onClick={handleAttachmentUploadClick}
                    disabled={
                      !canPost || isBuildingAttachment || !allowImages || lockNewThreads
                    }
                    title={
                      !allowImages
                        ? "Image uploads are disabled by admins."
                        : "Upload a JPG or PNG preview."
                    }
                  >
                    Upload image
                  </button>
                  <span className="community-forum__muted">
                    Up to {ATTACHMENT_LIMIT} attachments.
                  </span>
                </div>
                {attachmentError && (
                  <span className="community-forum__error">{attachmentError}</span>
                )}
                {threadAttachments.length > 0 && (
                  <div className="community-forum__attachments-grid">
                    {threadAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        className="community-forum__attachment-card"
                      >
                        <div className="community-forum__attachment-preview">
                          {attachment.previewUrl ? (
                            <img
                              src={attachment.previewUrl}
                              alt={attachment.title || "Attachment"}
                            />
                          ) : (
                            <span className="community-forum__muted">
                              Preview missing
                            </span>
                          )}
                        </div>
                        <div className="community-forum__attachment-meta">
                          <span>{attachment.title || "Attachment"}</span>
                          <button
                            className="btn danger"
                            type="button"
                            onClick={() => handleRemoveAttachment(attachment.id)}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="community-forum__composer-actions">
                {!canPost && (
                  <span className="community-forum__muted">
                    Sign in to create a thread.
                  </span>
                )}
                {lockNewThreads && (
                  <span className="community-forum__error">
                    New threads are paused by admins.
                  </span>
                )}
                {postError && (
                  <span className="community-forum__error">{postError}</span>
                )}
                <button
                  className="btn btn-primary"
                  type="button"
                  disabled={
                    !canPost || isSubmitting || lockNewThreads || isBuildingAttachment
                  }
                  onClick={handlePostSubmit}
                >
                  {isSubmitting ? "Posting..." : "Post"}
                </button>
              </div>
            </div>

            <div className="community-forum__threads">
              <div className="community-forum__board-controls">
                <span className="community-forum__muted">
                  {activeSectionLabel}
                  {activeSectionId !== "all" ? ` · ${activeSubsectionLabel}` : ""}
                  {searchQuery.trim() ? ` · "${searchQuery.trim()}"` : ""}
                </span>
                {threadsLoading && (
                  <span className="community-forum__muted">Loading...</span>
                )}
                {threadsError && (
                  <span className="community-forum__error">{threadsError}</span>
                )}
              </div>
              {threadItems.length === 0 && !threadsLoading && (
                <div className="community-forum__notice">
                  No threads yet. Start the first discussion or browse sample ideas.
                </div>
              )}
              {threadItems.map((thread) => (
                <article key={thread.id} className="community-forum__thread">
                  <div className="community-forum__thread-header">
                    <div>
                      <div className="community-forum__thread-tags">
                        <span className="community-forum__pill">
                          {thread.sectionLabel}
                        </span>
                        {thread.subsectionLabel && (
                          <span className="community-forum__pill is-subsection">
                            {thread.subsectionLabel}
                          </span>
                        )}
                        {thread.isPinned && (
                          <span className="community-forum__pill is-pin">
                            Pinned
                          </span>
                        )}
                        {thread.isLocked && (
                          <span className="community-forum__pill is-lock">
                            Locked
                          </span>
                        )}
                      </div>
                      <h4>{thread.title}</h4>
                    </div>
                    <span className="community-forum__thread-time">
                      {thread.time}
                    </span>
                  </div>
                  <p className="community-forum__thread-excerpt">
                    {thread.isDeleted
                      ? "This thread has been removed."
                      : thread.excerpt}
                  </p>
                  {!thread.isDeleted && thread.attachments.length > 0 && (
                    <div className="community-forum__thread-attachments">
                      {thread.attachments
                        .slice(0, 2)
                        .map((attachment, index) => (
                        <div
                          key={attachment.id || `${thread.id}-attachment-${index}`}
                          className="community-forum__thread-attachment"
                        >
                          {attachment.previewUrl ? (
                            <img
                              src={attachment.previewUrl}
                              alt={attachment.title || "Attachment"}
                            />
                          ) : (
                            <span className="community-forum__muted">
                              Attachment
                            </span>
                          )}
                        </div>
                      ))}
                      {thread.attachments.length > 2 && (
                        <span className="community-forum__thread-attachment-count">
                          +{thread.attachments.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="community-forum__thread-meta">
                    <span>
                      {thread.author} {thread.handle}
                    </span>
                    <span>
                      {thread.replies} replies | {thread.views} views
                    </span>
                  </div>
                  <div className="community-forum__thread-actions">
                    <button
                      className="btn"
                      type="button"
                      disabled={!user}
                      title={!user ? "Sign in to view threads." : ""}
                      onClick={() => handleOpenThread(thread.id)}
                    >
                      Open thread
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </main>

          <aside className="community-forum__aside">
            <div className="community-forum__panel">
              <h4>Trending tags</h4>
              <div className="community-forum__tags">
                {TRENDING_TAGS.map((tag) => (
                  <span key={tag} className="community-forum__tag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
            <div className="community-forum__panel">
              <h4>Forum status</h4>
              <p>
                Threads, replies, and gallery sharing are live. Moderators can
                pin or lock discussions as needed.
              </p>
            </div>
          </aside>
        </div>
        </>
      )}

      {activeTab === "board" && activeThreadId && (
        <div className="community-forum__layout community-forum__layout--thread">
          <main className="community-forum__main">
            <div className="community-forum__thread-detail">
              <div className="community-forum__thread-detail-header">
                <button className="btn" type="button" onClick={handleCloseThread}>
                  Back to board
                </button>
                <div className="community-forum__thread-tags">
                  {(activeThread?.sectionLabel || activeThread?.category) && (
                    <span className="community-forum__pill">
                      {activeThread.sectionLabel || activeThread.category}
                    </span>
                  )}
                  {activeThread?.isPinned && (
                    <span className="community-forum__pill is-pin">Pinned</span>
                  )}
                  {activeThread?.isLocked && (
                    <span className="community-forum__pill is-lock">Locked</span>
                  )}
                </div>
              </div>

              {!activeThread && (
                <div className="community-forum__notice">
                  Loading thread...
                </div>
              )}

              {activeThread && (
                <div className="community-forum__thread-card">
                  {editingThread ? (
                    <>
                      <input
                        className="input"
                        type="text"
                        value={editingThreadTitle}
                        onChange={(event) => setEditingThreadTitle(event.target.value)}
                      />
                      <textarea
                        className="input"
                        rows={4}
                        value={editingThreadBody}
                        onChange={(event) => setEditingThreadBody(event.target.value)}
                      />
                      {threadEditError && (
                        <span className="community-forum__error">
                          {threadEditError}
                        </span>
                      )}
                      <div className="community-forum__thread-actions">
                        <button className="btn btn-primary" type="button" onClick={handleThreadUpdate}>
                          Save changes
                        </button>
                        <button className="btn" type="button" onClick={() => setEditingThread(false)}>
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="community-forum__thread-tags">
                        <span className="community-forum__pill">
                          {activeThread.sectionLabel ||
                            activeThread.category ||
                            "General"}
                        </span>
                        {(activeThread.subsectionLabel || activeThread.subsectionId) && (
                          <span className="community-forum__pill is-subsection">
                            {activeThread.subsectionLabel || "General chat"}
                          </span>
                        )}
                      </div>
                      <h3>{activeThread.title}</h3>
                      <div className="community-forum__thread-meta">
                        <span>
                          {activeThread.authorName || "Community member"}{" "}
                          {activeThread.authorHandle ? `@${activeThread.authorHandle}` : ""}
                        </span>
                        <span>
                          {formatPostTime(activeThread.updatedAt || activeThread.createdAt)}
                        </span>
                      </div>
                      <div className="community-forum__thread-body community-forum__formatted-blocks">
                        {renderFormattedBlocks(
                          activeThread.body || "",
                          `thread-${activeThread.id || "active"}`
                        )}
                      </div>
                      {!activeThread.isDeleted &&
                        Array.isArray(activeThread.attachments) &&
                        activeThread.attachments.length > 0 && (
                          <div className="community-forum__attachments-grid">
                            {activeThread.attachments.map((attachment, index) => (
                              <div
                                key={
                                  attachment.id ||
                                  `${activeThread.id}-attachment-${index}`
                                }
                                className="community-forum__attachment-card"
                              >
                                <div className="community-forum__attachment-preview">
                                  {attachment.previewUrl ? (
                                    <img
                                      src={attachment.previewUrl}
                                      alt={attachment.title || "Attachment"}
                                    />
                                  ) : (
                                    <span className="community-forum__muted">
                                      Preview missing
                                    </span>
                                  )}
                                </div>
                                <div className="community-forum__attachment-meta">
                                  <span>{attachment.title || "Attachment"}</span>
                                  {attachment.type && (
                                    <span className="community-forum__muted">
                                      {attachment.type}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}

              <div className="community-forum__replies">
                <h4>Replies</h4>
                {commentsLoading && (
                  <span className="community-forum__muted">Loading replies...</span>
                )}
                {commentsError && (
                  <span className="community-forum__error">{commentsError}</span>
                )}
                {!commentsLoading && comments.length === 0 && (
                  <div className="community-forum__notice">
                    No replies yet. Start the conversation.
                  </div>
                )}
                {comments.map((comment) => {
                  const canEditComment =
                    !comment.isDeleted &&
                    ((user?.uid && comment.authorId === user.uid) || canModerate);
                  const isEditing = editingCommentId === comment.id;
                  return (
                    <div key={comment.id} className="community-forum__reply">
                      <div className="community-forum__reply-header">
                        <div>
                          <strong>{comment.authorName || "Community member"}</strong>{" "}
                          {comment.authorHandle ? `@${comment.authorHandle}` : ""}
                          {comment.authorRole && (
                            <span className="community-forum__role-badge">
                              {comment.authorRole}
                            </span>
                          )}
                        </div>
                        <span className="community-forum__thread-time">
                          {formatPostTime(comment.updatedAt || comment.createdAt)}
                        </span>
                      </div>
                      {comment.isDeleted ? (
                        <p className="community-forum__thread-excerpt">
                          Reply removed.
                        </p>
                      ) : isEditing ? (
                        <div className="community-forum__reply-edit">
                          <textarea
                            className="input"
                            rows={3}
                            value={editingCommentBody}
                            onChange={(event) => setEditingCommentBody(event.target.value)}
                          />
                          {commentEditError && (
                            <span className="community-forum__error">
                              {commentEditError}
                            </span>
                          )}
                          <div className="community-forum__thread-actions">
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => handleCommentEdit(comment.id)}
                            >
                              Save
                            </button>
                            <button
                              className="btn"
                              type="button"
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditingCommentBody("");
                                setCommentEditError("");
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="community-forum__thread-excerpt community-forum__formatted-blocks">
                          {renderFormattedBlocks(
                            comment.body || "",
                            `comment-${comment.id}`
                          )}
                        </div>
                      )}
                      {canEditComment && !isEditing && !comment.isDeleted && (
                        <div className="community-forum__reply-actions">
                          <button
                            className="btn"
                            type="button"
                            onClick={() => {
                              setEditingCommentId(comment.id);
                              setEditingCommentBody(comment.body || "");
                              setCommentEditError("");
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn danger"
                            type="button"
                            onClick={() => handleCommentRemove(comment.id)}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="community-forum__composer">
                  <h4>Reply to this thread</h4>
                  <div className="community-forum__composer-tools">
                    <span className="community-forum__muted">Formatting</span>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() =>
                        applyInlineFormat(commentBodyRef, setCommentBody, {
                          prefix: "**",
                          suffix: "**",
                          placeholder: "bold text",
                        })
                      }
                    >
                      Bold
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() =>
                        applyInlineFormat(commentBodyRef, setCommentBody, {
                          prefix: "*",
                          suffix: "*",
                          placeholder: "italic text",
                        })
                      }
                    >
                      Italic
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() =>
                        applyListFormat(commentBodyRef, setCommentBody, "unordered")
                      }
                    >
                      Bullets
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() =>
                        applyListFormat(commentBodyRef, setCommentBody, "ordered")
                      }
                    >
                      Numbered
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() => applyQuoteFormat(commentBodyRef, setCommentBody)}
                    >
                      Quote
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() =>
                        applyCodeBlockFormat(commentBodyRef, setCommentBody)
                      }
                    >
                      Code
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() => applyLinkFormat(commentBodyRef, setCommentBody)}
                    >
                      Link
                    </button>
                    <button
                      className="btn"
                      type="button"
                      disabled={!canPost || activeThread?.isLocked || lockReplies}
                      onClick={() => setShowReplyPreview((value) => !value)}
                    >
                      {showReplyPreview ? "Hide preview" : "Preview"}
                    </button>
                  </div>
                  <textarea
                    className="input"
                    rows={3}
                    placeholder={
                      lockReplies
                        ? "Replies are paused by admins."
                        : activeThread?.isLocked
                          ? "Thread is locked."
                          : "Write your reply"
                    }
                    value={commentBody}
                    onChange={(event) => setCommentBody(event.target.value)}
                    disabled={!canPost || activeThread?.isLocked || lockReplies}
                    ref={commentBodyRef}
                  />
                  {showReplyPreview && (
                    <div className="community-forum__format-preview community-forum__formatted-blocks">
                      {renderFormattedBlocks(commentBody, "reply-preview") || (
                        <span className="community-forum__muted">
                          Start typing to preview your formatting.
                        </span>
                      )}
                    </div>
                  )}
                  <div className="community-forum__composer-actions">
                    {!canPost && (
                      <span className="community-forum__muted">
                        Sign in to reply.
                      </span>
                    )}
                    {lockReplies && (
                      <span className="community-forum__error">
                        Replies are paused by admins.
                      </span>
                    )}
                    {commentError && (
                      <span className="community-forum__error">{commentError}</span>
                    )}
                    <button
                      className="btn btn-primary"
                      type="button"
                      disabled={
                        !canPost ||
                        isSubmittingComment ||
                        activeThread?.isLocked ||
                        lockReplies
                      }
                      onClick={handleCommentSubmit}
                    >
                      {isSubmittingComment ? "Posting..." : "Reply"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>

          <aside className="community-forum__aside">
            <div className="community-forum__panel">
              <h4>Thread actions</h4>
              <div className="community-forum__thread-actions">
                {canEditThread && (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => setEditingThread(true)}
                  >
                    Edit thread
                  </button>
                )}
                {canModerate && (
                  <>
                    <button
                      className="btn"
                      type="button"
                      onClick={() =>
                        handleThreadModeration({ isPinned: !activeThread?.isPinned })
                      }
                    >
                      {activeThread?.isPinned ? "Unpin" : "Pin"} thread
                    </button>
                    <button
                      className="btn"
                      type="button"
                      onClick={() =>
                        handleThreadModeration({ isLocked: !activeThread?.isLocked })
                      }
                    >
                      {activeThread?.isLocked ? "Unlock" : "Lock"} thread
                    </button>
                  </>
                )}
                {canRemoveThread && (
                  <button className="btn danger" type="button" onClick={handleThreadRemove}>
                    Remove thread
                  </button>
                )}
              </div>
            </div>
            <div className="community-forum__panel">
              <h4>Thread info</h4>
              <p>
                Replies: {formatCount(activeThread?.replyCount || 0)}
                <br />
                Last reply: {formatPostTime(activeThread?.lastReplyAt)}
              </p>
            </div>
          </aside>
        </div>
      )}

      {activeTab === "gallery" && (
        <div className="community-forum__layout community-forum__layout--gallery">
          <aside className="community-forum__sidebar">
            <div className="community-forum__panel">
              <h4>Share a design</h4>
              <p>
                Share the design you have open. Load a saved design and come
                back here to publish it.
              </p>
              {!allowGallery && (
                <div className="community-forum__notice">
                  Gallery sharing is currently paused by admins.
                </div>
              )}
              <input
                className="input"
                type="text"
                placeholder="Design name"
                value={shareTitle}
                onChange={(event) => setShareTitle(event.target.value)}
                disabled={!user || !allowGallery}
              />
              <textarea
                className="input"
                rows={3}
                placeholder="Add a short description (optional)"
                value={shareDescription}
                onChange={(event) => setShareDescription(event.target.value)}
                disabled={!user || !allowGallery}
              />
              {shareError && (
                <span className="community-forum__error">{shareError}</span>
              )}
              {!user && (
                <span className="community-forum__muted">
                  Sign in to share to the gallery.
                </span>
              )}
              <button
                className="btn btn-primary"
                type="button"
                disabled={!user || isSharing || !allowGallery}
                onClick={handleShareDesign}
              >
                {isSharing ? "Sharing..." : "Share design"}
              </button>
            </div>
            {user && (
              <div className="community-forum__panel">
                <h4>My shared designs</h4>
                <div className="community-forum__share-list">
                  {galleryItems.filter((item) => item.authorId === user.uid).length === 0 && (
                    <span className="community-forum__muted">
                      No shared designs yet.
                    </span>
                  )}
                  {galleryItems
                    .filter((item) => item.authorId === user.uid)
                    .map((item) => (
                      <div key={item.id} className="community-forum__share-row">
                        <span>{item.designName || "Untitled"}</span>
                        <button
                          className="btn danger"
                          type="button"
                          onClick={() => handleUnshareDesign(item.id)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </aside>

          <main className="community-forum__main">
            <div className="community-forum__gallery-header">
              <h3>Community gallery</h3>
              {galleryLoading && (
                <span className="community-forum__muted">Loading...</span>
              )}
              {galleryError && (
                <span className="community-forum__error">{galleryError}</span>
              )}
              {activeGalleryAuthor && (
                <div className="community-forum__filter-pill">
                  Showing designs by{" "}
                  <strong>
                    {activeGalleryAuthor.label}
                    {activeGalleryAuthor.handleLabel
                      ? ` ${activeGalleryAuthor.handleLabel}`
                      : ""}
                  </strong>
                  <button className="btn" type="button" onClick={clearGalleryFilter}>
                    Clear
                  </button>
                </div>
              )}
            </div>
            {filteredGalleryFeed.length === 0 && !galleryLoading && (
              <div className="community-forum__notice">
                The gallery is empty. Share a design to get started.
              </div>
            )}
            <div className="community-forum__gallery-grid">
              {filteredGalleryFeed.map((item) => (
                <article key={item.id} className="community-forum__gallery-card">
                  <div className="community-forum__gallery-preview">
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt={item.designName || "Design preview"} />
                    ) : (
                      <div className="community-forum__gallery-placeholder">
                        Preview unavailable
                      </div>
                    )}
                  </div>
                  <div className="community-forum__gallery-body">
                    <h4>{item.designName || "Untitled design"}</h4>
                    <p className="community-forum__muted">
                      {item.authorName || "Community member"}{" "}
                      {item.authorHandle ? `@${item.authorHandle}` : ""}
                    </p>
                    <div className="community-forum__gallery-meta">
                      <span>
                        {item.cols || "-"}x{item.rows || "-"}
                      </span>
                      <span>{formatCount(item.paletteCount || 0)} colors</span>
                      <span>{formatCount(item.stitchCount || 0)} stitches</span>
                    </div>
                    <div className="community-forum__gallery-footer">
                      <span className="community-forum__thread-time">
                        {formatPostTime(item.updatedAt || item.createdAt)}
                      </span>
                      {user && item.authorId === user.uid && (
                        <button
                          className="btn danger"
                          type="button"
                          onClick={() => handleUnshareDesign(item.id)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </main>
        </div>
      )}

      {activeTab === "moderation" && isAdmin && (
        <div className="community-forum__layout community-forum__layout--moderation">
          <main className="community-forum__main">
            <div className="community-forum__panel">
              <h4>Role management</h4>
              <p className="community-forum__muted">
                Promote members to moderators or admins. User ids come from
                profile documents.
              </p>
              <div className="community-forum__moderation-form">
                <input
                  className="input"
                  type="text"
                  placeholder="User id"
                  value={manualRoleUid}
                  onChange={(event) => setManualRoleUid(event.target.value)}
                />
                <select
                  className="input"
                  value={manualRole}
                  onChange={(event) => setManualRole(event.target.value)}
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <button className="btn btn-primary" type="button" onClick={handleManualRoleSubmit}>
                  Update role
                </button>
              </div>
              {roleUpdateStatus && (
                <span className="community-forum__muted">{roleUpdateStatus}</span>
              )}
              {roleUpdateError && (
                <span className="community-forum__error">{roleUpdateError}</span>
              )}
            </div>

            <div className="community-forum__panel">
              <h4>Recent members</h4>
              {uniqueMembers.length === 0 && (
                <span className="community-forum__muted">
                  No recent members yet.
                </span>
              )}
              <div className="community-forum__moderation-list">
                {uniqueMembers.map((member) => (
                  <div key={member.id} className="community-forum__moderation-row">
                    <div>
                      <strong>{member.name}</strong>
                      {member.handle && (
                        <span className="community-forum__muted"> {member.handle}</span>
                      )}
                      <div className="community-forum__muted">
                        {member.id}
                      </div>
                    </div>
                    <div className="community-forum__moderation-actions">
                      <select
                        className="input"
                        value={profileRoles[member.id] || "member"}
                        onChange={(event) => handleRoleUpdate(member.id, event.target.value)}
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </main>
          <aside className="community-forum__aside">
            <div className="community-forum__panel">
              <h4>Moderator privileges</h4>
              <ul className="community-forum__simple-list">
                <li>Pin or lock threads from the thread detail view.</li>
                <li>Remove disruptive replies and threads.</li>
                <li>Promote trusted members to moderation.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}

      {activeTab === "settings" && isAdmin && (
        <div className="community-forum__layout community-forum__layout--settings">
          <main className="community-forum__main">
            <div className="community-forum__panel">
              <h4>Forum controls</h4>
              <div className="community-forum__setting-list">
                {settingsRows.map((row) => (
                  <label key={row.key} className="community-forum__setting-row">
                    <input
                      type="checkbox"
                      checked={Boolean(row.value)}
                      onChange={(event) =>
                        handleForumToggle(row.key, event.target.checked)
                      }
                    />
                    <div>
                      <div className="community-forum__setting-label">
                        {row.label}
                      </div>
                      <div className="community-forum__setting-description">
                        {row.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div className="community-forum__panel">
              <h4>Forum branding</h4>
              <div className="community-forum__setting-fields">
                <label>
                  <span className="community-forum__setting-label">Forum name</span>
                  <input
                    className="input"
                    type="text"
                    value={settingsDraft.forumName}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        forumName: event.target.value,
                      }))
                    }
                  />
                </label>
                <label>
                  <span className="community-forum__setting-label">Announcement</span>
                  <textarea
                    className="input"
                    rows={3}
                    value={settingsDraft.announcement}
                    onChange={(event) =>
                      setSettingsDraft((prev) => ({
                        ...prev,
                        announcement: event.target.value,
                      }))
                    }
                    placeholder="Share a short forum update or rule reminder."
                  />
                </label>
                <button className="btn btn-primary" type="button" onClick={handleSettingsSave}>
                  Save updates
                </button>
                {settingsStatus && (
                  <span className="community-forum__muted">{settingsStatus}</span>
                )}
                {settingsError && (
                  <span className="community-forum__error">{settingsError}</span>
                )}
              </div>
            </div>
          </main>
          <aside className="community-forum__aside">
            <div className="community-forum__panel">
              <h4>Admin tips</h4>
              <ul className="community-forum__simple-list">
                <li>Lock new threads before maintenance windows.</li>
                <li>Use announcements for seasonal events or rules.</li>
                <li>Disable gallery sharing when you need moderation time.</li>
              </ul>
            </div>
          </aside>
        </div>
      )}
    </section>
  );
}
